import type { ApiError } from "@/types";
import { env } from "@services/config/env";

/**
 * HTTP client for the legacy Java EE backend.
 *
 * The legacy backend wraps all responses in:
 *   { status: number, message: string, data: string }
 * where `data` is a JSON-encoded string that must be parsed separately.
 *
 * This client handles that double-parsing automatically.
 */
class LegacyHttpClient {
  private baseUrl: string;
  private tokenGetter: (() => string | null) | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setTokenGetter(getter: () => string | null): void {
    this.tokenGetter = getter;
  }

  async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    const url = this.buildUrl(path, params);
    return this.request<T>(url, { method: "GET" });
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    const url = this.buildUrl(path);
    return this.request<T>(url, {
      method: "POST",
      headers: { "Content-Type": "application/json;charset=UTF-8" },
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  private buildUrl(path: string, params?: Record<string, string>): string {
    const isAbsoluteUrl = /^https?:\/\//i.test(path);
    const normalizedBaseUrl = this.baseUrl.endsWith("/")
      ? this.baseUrl
      : `${this.baseUrl}/`;
    const normalizedPath = path.replace(/^\/+/, "");
    // Handle both absolute URLs (e.g., https://...) and relative paths (/api/...)
    const url = isAbsoluteUrl
      ? new URL(path)
      : new URL(normalizedPath, normalizedBaseUrl);
    if (params) {
      Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    }
    return url.toString();
  }

  private async request<T>(url: string, init: RequestInit): Promise<T> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...(init.headers as Record<string, string>),
    };

    const token = this.tokenGetter?.();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    if (env.apiToken) {
      headers["X-Api-Token"] = env.apiToken;
    }

    try {
      const response = await fetch(url, { ...init, headers });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const apiError: ApiError = {
          status: response.status,
          message:
            (errorBody as Record<string, string>).message ||
            response.statusText,
        };
        throw apiError;
      }

      const json = await response.json();

      // Legacy Java envelope: { status, message, data }
      // Always throw on status != 200 — data can be string, number, null or object
      if (this.isLegacyEnvelope(json)) {
        const legacy = json as { status: number; message: string; data: unknown };
        if (legacy.status !== 200) {
          throw { status: legacy.status, message: legacy.message } as ApiError;
        }
        if (typeof legacy.data === "number") {
          return legacy.data as unknown as T;
        }
        if (typeof legacy.data === "string") {
          try {
            return JSON.parse(legacy.data) as T;
          } catch {
            return legacy.data as unknown as T;
          }
        }
        return legacy.data as unknown as T;
      }

      // NestJS-style envelope: { success, data, message }
      if ("success" in json && "data" in json) {
        return json.data as T;
      }

      return json as T;
    } catch (error) {
      if (this.isApiError(error)) throw error;
      const apiError: ApiError = {
        status: 0,
        message:
          error instanceof Error ? error.message : "Error de red desconocido",
        code: "NETWORK_ERROR",
      };
      throw apiError;
    }
  }

  private isLegacyEnvelope(json: unknown): boolean {
    if (typeof json !== "object" || json === null) return false;
    // Detect the legacy Java envelope by shape: {status, message, data}
    // data can be string, number, null, or object — accept any
    return "status" in json && "message" in json && "data" in json;
  }

  private isApiError(error: unknown): error is ApiError {
    return (
      typeof error === "object" &&
      error !== null &&
      "status" in error &&
      "message" in error
    );
  }
}

/** Singleton legacy HTTP client for the Java backend. */
export const legacyHttpClient = new LegacyHttpClient(env.legacyApiBaseUrl);
